/**
 * Admin → India Moats
 *
 * Phase 6 - Aadhaar Vault tokenisation, RBI Account Aggregator (AA)
 * integration, DigiLocker break-glass policy. Three independent
 * sub-configs per tenant; each enabled/configured separately.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  ShieldCheck,
  KeyRound,
  Network,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Save,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  getIndiaMoatsStatus,
  configureIndiaMoat,
  type AadhaarVaultConfig,
  type AccountAggregatorConfig,
  type DigilockerBreakGlassConfig,
  type IndiaMoatsConfig,
} from '@/lib/api';

export function AdminIndiaMoats(): JSX.Element {
  const [config, setConfig] = useState<IndiaMoatsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMoat, setSuccessMoat] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getIndiaMoatsStatus();
      setConfig(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load India moats config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function save(
    moat: 'aadhaar-vault' | 'account-aggregator' | 'digilocker',
    payload: AadhaarVaultConfig | AccountAggregatorConfig | DigilockerBreakGlassConfig,
  ): Promise<void> {
    setError(null);
    try {
      const updated = await configureIndiaMoat(moat, payload);
      setConfig(updated);
      setSuccessMoat(moat);
      setTimeout(() => setSuccessMoat(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to save');
    }
  }

  if (loading) {
    return (
      <PageContainer>
        <GlassCard className="p-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> loading India moats config…
        </GlassCard>
      </PageContainer>
    );
  }

  if (!config) return <PageContainer>No config</PageContainer>;

  const enabledCount =
    (config.aadhaarVault.enabled ? 1 : 0) +
    (config.accountAggregator.enabled ? 1 : 0) +
    (config.digilockerBreakGlass.enabled ? 1 : 0);

  return (
    <PageContainer>
      <PageHeader
        title="Admin · India Moats"
        subtitle="Aadhaar Vault tokenisation (UIDAI ref arch), Account Aggregator (RBI AA framework), DigiLocker break-glass (citizen-verified emergency access). Three independent sub-configs - enable individually."
        badges={[
          { label: `${enabledCount}/3 moats enabled`, variant: enabledCount === 3 ? 'success' : 'glass' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AadhaarVaultCard
          config={config.aadhaarVault}
          onSave={(v) => save('aadhaar-vault', v)}
          showSuccess={successMoat === 'aadhaar-vault'}
        />
        <AccountAggregatorCard
          config={config.accountAggregator}
          onSave={(v) => save('account-aggregator', v)}
          showSuccess={successMoat === 'account-aggregator'}
        />
        <DigilockerCard
          config={config.digilockerBreakGlass}
          onSave={(v) => save('digilocker', v)}
          showSuccess={successMoat === 'digilocker'}
        />
      </div>
    </PageContainer>
  );
}

// ── Aadhaar Vault ────────────────────────────────────────────────────

function AadhaarVaultCard({
  config,
  onSave,
  showSuccess,
}: {
  config: AadhaarVaultConfig;
  onSave: (v: AadhaarVaultConfig) => Promise<void>;
  showSuccess: boolean;
}): JSX.Element {
  const [enabled, setEnabled] = useState(config.enabled);
  const [protectedFields, setProtectedFields] = useState((config.protectedFields ?? []).join(', '));
  const [tokenizationProvider, setTokenizationProvider] = useState(
    config.tokenizationProvider ?? '',
  );
  const [vaultKmsKeyRef, setVaultKmsKeyRef] = useState(config.vaultKmsKeyRef ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const payload: AadhaarVaultConfig = { enabled };
      const fields = protectedFields
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);
      if (fields.length > 0) payload.protectedFields = fields;
      if (tokenizationProvider.trim()) payload.tokenizationProvider = tokenizationProvider.trim();
      if (vaultKmsKeyRef.trim()) payload.vaultKmsKeyRef = vaultKmsKeyRef.trim();
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  }

  return (
    <MoatCard
      icon={<KeyRound className="h-4 w-4 text-primary" />}
      title="Aadhaar Vault"
      enabled={config.enabled}
      description="UIDAI Aadhaar Authentication Regulations 2023 - Aadhaar plaintext storage prohibited outside vault. Discovery alerts on plaintext detection; DAM blocks DML writes of plaintext Aadhaar."
      showSuccess={showSuccess}
    >
      <Toggle label="Enable Aadhaar Vault" checked={enabled} onChange={setEnabled} />
      <Field
        label="Protected fields (comma-separated column names)"
        value={protectedFields}
        onChange={setProtectedFields}
        placeholder="customer_aadhaar, applicant_aadhaar, kyc_uid"
      />
      <Field
        label="Tokenisation provider"
        value={tokenizationProvider}
        onChange={setTokenizationProvider}
        placeholder="e.g. uidai-reference-arch, vendor-x-vault"
      />
      <Field
        label="Vault KMS key ref"
        value={vaultKmsKeyRef}
        onChange={setVaultKmsKeyRef}
        placeholder="arn:aws:kms:..."
        mono
      />
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        Save Aadhaar Vault config
      </Button>
    </MoatCard>
  );
}

// ── Account Aggregator ──────────────────────────────────────────────

function AccountAggregatorCard({
  config,
  onSave,
  showSuccess,
}: {
  config: AccountAggregatorConfig;
  onSave: (v: AccountAggregatorConfig) => Promise<void>;
  showSuccess: boolean;
}): JSX.Element {
  const [enabled, setEnabled] = useState(config.enabled);
  const [fip, setFip] = useState(config.fipEndpointUrl ?? '');
  const [fiu, setFiu] = useState(config.fiuEndpointUrl ?? '');
  const [callback, setCallback] = useState(config.callbackUrl ?? '');
  const [kmsRef, setKmsRef] = useState(config.kmsKeyRef ?? '');
  const [consentDays, setConsentDays] = useState(config.consentMaxDays ?? 90);
  const [saving, setSaving] = useState(false);

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const payload: AccountAggregatorConfig = { enabled };
      if (fip.trim()) payload.fipEndpointUrl = fip.trim();
      if (fiu.trim()) payload.fiuEndpointUrl = fiu.trim();
      if (callback.trim()) payload.callbackUrl = callback.trim();
      if (kmsRef.trim()) payload.kmsKeyRef = kmsRef.trim();
      payload.consentMaxDays = consentDays;
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  }

  return (
    <MoatCard
      icon={<Network className="h-4 w-4 text-primary" />}
      title="Account Aggregator (AA)"
      enabled={config.enabled}
      description="RBI Account Aggregator framework - consent-based, purpose-specific financial data access. FIP/FIU endpoints, callback URL, consent expiry tracking."
      showSuccess={showSuccess}
    >
      <Toggle label="Enable AA integration" checked={enabled} onChange={setEnabled} />
      <Field label="FIP endpoint URL" value={fip} onChange={setFip} placeholder="https://aa-fip.example.com" mono />
      <Field label="FIU endpoint URL" value={fiu} onChange={setFiu} placeholder="https://aa-fiu.example.com" mono />
      <Field
        label="Callback URL"
        value={callback}
        onChange={setCallback}
        placeholder="https://discovery.foctta.com/v1/aa/callback"
        mono
      />
      <Field
        label="KMS key ref"
        value={kmsRef}
        onChange={setKmsRef}
        placeholder="arn:aws:kms:..."
        mono
      />
      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
          Consent max days
        </label>
        <input
          type="number"
          min={1}
          max={365}
          value={consentDays}
          onChange={(e) => setConsentDays(Number(e.target.value))}
          className="w-full glass-lite rounded-md p-2 text-xs outline-none"
        />
      </div>
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        Save AA config
      </Button>
    </MoatCard>
  );
}

// ── DigiLocker break-glass ──────────────────────────────────────────

function DigilockerCard({
  config,
  onSave,
  showSuccess,
}: {
  config: DigilockerBreakGlassConfig;
  onSave: (v: DigilockerBreakGlassConfig) => Promise<void>;
  showSuccess: boolean;
}): JSX.Element {
  const [enabled, setEnabled] = useState(config.enabled);
  const [approvers, setApprovers] = useState((config.breakGlassApprovers ?? []).join(', '));
  const [maxMinutes, setMaxMinutes] = useState(config.sessionMaxMinutes ?? 60);
  const [reasonCodes, setReasonCodes] = useState((config.allowedReasonCodes ?? []).join(', '));
  const [saving, setSaving] = useState(false);

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const payload: DigilockerBreakGlassConfig = { enabled };
      const approverList = approvers
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean);
      const codes = reasonCodes
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
      if (approverList.length > 0) payload.breakGlassApprovers = approverList;
      if (codes.length > 0) payload.allowedReasonCodes = codes;
      payload.sessionMaxMinutes = maxMinutes;
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  }

  return (
    <MoatCard
      icon={<ShieldCheck className="h-4 w-4 text-primary" />}
      title="DigiLocker break-glass"
      enabled={config.enabled}
      description="Emergency access via DigiLocker-verified citizen identity. Approver list + session cap + permitted reason codes scope the blast radius."
      showSuccess={showSuccess}
    >
      <Toggle label="Enable DigiLocker break-glass" checked={enabled} onChange={setEnabled} />
      <Field
        label="Approvers (comma-separated user UUIDs)"
        value={approvers}
        onChange={setApprovers}
        placeholder="uuid-1, uuid-2"
        mono
      />
      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
          Session max minutes
        </label>
        <input
          type="number"
          min={5}
          max={240}
          value={maxMinutes}
          onChange={(e) => setMaxMinutes(Number(e.target.value))}
          className="w-full glass-lite rounded-md p-2 text-xs outline-none"
        />
      </div>
      <Field
        label="Allowed reason codes (comma-separated)"
        value={reasonCodes}
        onChange={setReasonCodes}
        placeholder="incident_response, regulator_query, audit_request"
      />
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        Save DigiLocker config
      </Button>
    </MoatCard>
  );
}

// ── Shared sub-components ───────────────────────────────────────────

function MoatCard({
  icon,
  title,
  description,
  enabled,
  showSuccess,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  showSuccess: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <GlassCard className={enabled ? 'p-5 border-success/40 glow-low' : 'p-5'}>
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2">
            {icon}
            {title}
          </span>
          {enabled ? (
            <Badge variant="success" className="text-[10px] gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Enabled
            </Badge>
          ) : (
            <Badge variant="glass" className="text-[10px]">
              Disabled
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <div className="space-y-3">{children}</div>
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 flex items-center gap-1.5 text-[11px] text-success"
        >
          <CheckCircle2 className="h-3 w-3" />
          Saved.
        </motion.div>
      )}
    </GlassCard>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between glass-lite rounded-md p-2.5">
      <span className="text-xs">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={
          checked
            ? 'h-5 w-10 rounded-full bg-success/60 relative transition-colors'
            : 'h-5 w-10 rounded-full bg-muted/40 relative transition-colors'
        }
      >
        <span
          className={
            checked
              ? 'absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform'
              : 'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform'
          }
        />
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={
          'w-full glass-lite rounded-md p-2 text-xs outline-none ' + (mono ? 'font-mono' : '')
        }
      />
    </div>
  );
}
